import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { EnglishLanguageButton } from "@/components/english-language-button";

const advisoryChildren = [
  { to: "/services", label: "Services" },
  { to: "/sela", label: "SELA" },
  { to: "/aela", label: "AELA" },
] as const;

const insightsChildren = [
  { to: "/blog", label: "Blogs" },
  { to: "/salesforce-procurement", label: "Salesforce Procurement Advisory" },
] as const;

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [advisoryOpen, setAdvisoryOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [mobileAdvisoryOpen, setMobileAdvisoryOpen] = useState(false);
  const [mobileInsightsOpen, setMobileInsightsOpen] = useState(false);
  const advisoryRef = useRef<HTMLDivElement | null>(null);
  const insightsRef = useRef<HTMLDivElement | null>(null);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    if (!advisoryOpen && !insightsOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (advisoryRef.current && !advisoryRef.current.contains(target)) {
        setAdvisoryOpen(false);
      }
      if (insightsRef.current && !insightsRef.current.contains(target)) {
        setInsightsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setAdvisoryOpen(false);
        setInsightsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [advisoryOpen, insightsOpen]);

  const advisoryActive = advisoryChildren.some((item) => item.to === pathname);
  const insightsActive = insightsChildren.some((item) => item.to === pathname);

  const linkClass =
    "-my-3 py-3 text-sm text-muted-foreground transition-[color,opacity] duration-200 hover:text-foreground";

  return (
    <header className="sticky top-0 z-50 border-b border-[#1a1408]/10 bg-[#fae396]/95 backdrop-blur-md">
      <div className="container-x flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center max-lg:min-h-12" aria-label="SaaSed home" title="SaaSed home">
          <span
            className="font-display text-2xl font-bold leading-none tracking-tight text-foreground"
            style={{ fontFamily: '"Bricolage Grotesque", ui-serif, Georgia, serif' }}
          >
            SaaSed.co
          </span>
        </Link>

        <nav aria-label="Main" itemScope itemType="https://schema.org/SiteNavigationElement" className="hidden items-center gap-8 lg:flex">
          <Link
            to="/"
            title="Home"
            aria-label="Home"
            itemProp="url"
            className={linkClass}
            style={{ willChange: "transform, opacity" }}
            activeProps={{ className: "text-foreground font-medium" }}
            activeOptions={{ exact: true }}
          >
            Home
          </Link>

          <div className="relative" ref={advisoryRef}>
            <button
              type="button"
              aria-label={advisoryOpen ? "Collapse Advisory Services menu" : "Expand Advisory Services menu"}
              aria-expanded={advisoryOpen}
              aria-controls="advisory-submenu"
              onClick={() => {
                setAdvisoryOpen((v) => !v);
                setInsightsOpen(false);
              }}
              className={`${linkClass} inline-flex items-center gap-1 whitespace-nowrap ${advisoryActive ? "font-medium text-foreground" : ""}`}
              style={{ willChange: "transform, opacity" }}
            >
              <span>Advisory Services</span>
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`transition-transform duration-200 ${advisoryOpen ? "rotate-180" : ""}`}
              />
            </button>

            <div
              id="advisory-submenu"
              hidden={!advisoryOpen}
              className="absolute left-0 top-full z-50 mt-4 min-w-[180px] rounded-xl border border-[#1a1408]/10 bg-[#fffdf0] py-2 shadow-soft"
            >
              {advisoryChildren.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  aria-label={item.label}
                  itemProp="url"
                  onClick={() => setAdvisoryOpen(false)}
                  className="block px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  activeProps={{ className: "block px-4 py-2 text-sm text-foreground font-medium" }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="relative" ref={insightsRef}>
            <button
              type="button"
              aria-label={insightsOpen ? "Collapse Insights menu" : "Expand Insights menu"}
              aria-expanded={insightsOpen}
              aria-controls="insights-submenu"
              onClick={() => {
                setInsightsOpen((v) => !v);
                setAdvisoryOpen(false);
              }}
              className={`${linkClass} inline-flex items-center gap-1 whitespace-nowrap ${insightsActive ? "font-medium text-foreground" : ""}`}
              style={{ willChange: "transform, opacity" }}
            >
              <span>Insights</span>
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`transition-transform duration-200 ${insightsOpen ? "rotate-180" : ""}`}
              />
            </button>

            <div
              id="insights-submenu"
              hidden={!insightsOpen}
              className="absolute left-0 top-full z-50 mt-4 min-w-[260px] rounded-xl border border-[#1a1408]/10 bg-[#fffdf0] py-2 shadow-soft"
            >
              {insightsChildren.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  aria-label={item.label}
                  itemProp="url"
                  onClick={() => setInsightsOpen(false)}
                  className="block px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  activeProps={{ className: "block px-4 py-2 text-sm text-foreground font-medium" }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          <Link
            to="/about"
            title="About"
            aria-label="About"
            itemProp="url"
            className={linkClass}
            style={{ willChange: "transform, opacity" }}
            activeProps={{ className: "text-foreground font-medium" }}
          >
            About
          </Link>
          <Link
            to="/contact"
            title="Contact"
            aria-label="Contact"
            itemProp="url"
            className={linkClass}
            style={{ willChange: "transform, opacity" }}
            activeProps={{ className: "text-foreground font-medium" }}
          >
            Contact
          </Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <EnglishLanguageButton />
          <Link
            to="/contact"
            aria-label="Contact SaaSed"
            title="Contact SaaSed"
            className="rounded-full bg-[#1a1408] px-5 py-2 text-[#fffdf0] shadow-soft transition-transform hover:-translate-y-0.5 inline-flex whitespace-nowrap font-sans text-sm font-medium uppercase tracking-[0.15em]"
            style={{ color: "#fffdf0" }}
          >
            CONTACT US
          </Link>
        </div>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
          className="inline-flex items-center justify-center rounded-md p-2 lg:hidden max-lg:min-h-12 max-lg:min-w-12"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
        </button>
      </div>

      <div id="mobile-nav" hidden={!open} className="border-t border-[#1a1408]/10 bg-[#fae396] lg:hidden">
        <nav aria-label="Mobile" className="container-x flex flex-col gap-1 py-4">
            <Link
              to="/"
              title="Home"
              aria-label="Home"
              className="flex items-center rounded-md px-2 py-3 text-sm text-foreground min-h-12"
              onClick={() => setOpen(false)}
            >
              Home
            </Link>

            <button
              type="button"
              aria-label={mobileAdvisoryOpen ? "Collapse Advisory Services menu" : "Expand Advisory Services menu"}
              aria-expanded={mobileAdvisoryOpen}
              aria-controls="mobile-advisory-submenu"
              onClick={() => setMobileAdvisoryOpen((v) => !v)}
              className="flex items-center justify-between rounded-md px-2 py-3 text-sm text-foreground min-h-12"
            >
              <span>Advisory Services</span>
              <ChevronDown
                size={18}
                aria-hidden="true"
                className={`transition-transform duration-200 ${mobileAdvisoryOpen ? "rotate-180" : ""}`}
              />
            </button>

            <div id="mobile-advisory-submenu" hidden={!mobileAdvisoryOpen} className="flex flex-col gap-1">
              {advisoryChildren.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  aria-label={item.label}
                  className="flex items-center rounded-md px-2 py-3 pl-6 text-sm text-muted-foreground min-h-12"
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <button
              type="button"
              aria-label={mobileInsightsOpen ? "Collapse Insights menu" : "Expand Insights menu"}
              aria-expanded={mobileInsightsOpen}
              aria-controls="mobile-insights-submenu"
              onClick={() => setMobileInsightsOpen((v) => !v)}
              className="flex items-center justify-between rounded-md px-2 py-3 text-sm text-foreground min-h-12"
            >
              <span>Insights</span>
              <ChevronDown
                size={18}
                aria-hidden="true"
                className={`transition-transform duration-200 ${mobileInsightsOpen ? "rotate-180" : ""}`}
              />
            </button>

            <div id="mobile-insights-submenu" hidden={!mobileInsightsOpen} className="flex flex-col gap-1">
              {insightsChildren.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  aria-label={item.label}
                  className="flex items-center rounded-md px-2 py-3 pl-6 text-sm text-muted-foreground min-h-12"
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <Link
              to="/about"
              title="About"
              aria-label="About"
              className="flex items-center rounded-md px-2 py-3 text-sm text-foreground min-h-12"
              onClick={() => setOpen(false)}
            >
              About
            </Link>
            <Link
              to="/contact"
              title="Contact"
              aria-label="Contact"
              className="flex items-center rounded-md px-2 py-3 text-sm text-foreground min-h-12"
              onClick={() => setOpen(false)}
            >
              Contact
            </Link>

            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              aria-label="Contact SaaSed"
              title="Contact SaaSed"
              className="mt-2 inline-flex min-h-12 items-center justify-center rounded-full bg-[#1a1408] px-5 py-3 text-center text-[#fffdf0] font-sans text-sm font-medium uppercase tracking-[0.15em]"
              style={{ color: "#fffdf0" }}
            >
              CONTACT US
            </Link>
            <EnglishLanguageButton className="mt-2 justify-center" />
        </nav>
      </div>
    </header>
  );
}
